import { FC, useMemo, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import classNames from "classnames";
import { formatFullName } from "../../../utils/functions";
import {
  EVENT_RESULT_CALC_TYPES_OPTIONS,
  EVENT_RESULT_SKIP_OPTIONS,
  EVENT_RESULT_TYPES_OPTIONS,
} from "../../../constants/config";
import {
  EVENT_RESULT_CALC_TYPES,
  EVENT_RESULT_TYPES,
  EVENT_RESULT_UNITS,
} from "../../../enums/eventresults";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { useToastAlert } from "../../../hooks/useToastAlert/useToastAlert";
import { addBaseUrl } from "../../../lib/DurationPicker/utils";
import { IEvent } from "../../../models/event";
import { IDropJumpResult, IEventResult } from "../../../models/eventResult";
import CustomAvatar from "../../../shared/CustomAvatar/CustomAvatar";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import CustomTextInput from "../../../shared/CustomInput/CustomTextInput";
import CustomSelectInput from "../../../shared/CustomSelectInput/CustomSelectInput";
import Typhography from "../../../shared/Typhography/Typhography";
import style from "./CompileEvent.module.scss";

// ── Types ────────────────────────────────────────────────────────────────────

type AttemptData = {
  boxHeight: number | null;
  rsi: number | null;
  contactTime: number | null;
  jumpHeight: number | null;
};

type AthleteData = {
  userId: string;
  firstName: string;
  lastName: string;
  image?: string;
  unit: string;
  calcType: string;
  skipped: string | undefined;
  attempts: AttemptData[];
};

export const MAX_DJ_ATTEMPTS = 3;

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildEmptyAttempts = (): AttemptData[] =>
  Array.from({ length: MAX_DJ_ATTEMPTS }, () => ({
    boxHeight: null,
    rsi: null,
    contactTime: null,
    jumpHeight: null,
  }));

// ── CompileDropJumpEvent ─────────────────────────────────────────────────────

export const CompileDropJumpEvent: FC<{
  event: IEvent;
  eventResults: IEventResult[];
  onClose: () => void;
}> = ({ event, eventResults, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const toastAlert = useToastAlert();
  const [statusUser, setStatusUser] = useState<
    Record<string, "success" | "error" | "loading">
  >({});
  const endpoints = useEndpoints();
  const [search, setSearch] = useState("");

  // ── State: one entry per athlete ──────────────────────────────────────────

  const [athletes, setAthletes] = useState<AthleteData[]>(() =>
    event.userIds.map((user) => {
      const er = eventResults.find((i) => i.userId === user._id) as
        | IDropJumpResult
        | undefined;
      const savedAttempts = er?.dropJumpValue || [];
      const attempts = buildEmptyAttempts().map((empty, i) => {
        const saved = savedAttempts[i];
        if (!saved) return empty;
        return {
          boxHeight: saved.boxHeight ?? null,
          rsi: saved.rsi ?? null,
          contactTime: saved.contactTime ?? null,
          jumpHeight: saved.jumpHeight ?? null,
        };
      });
      return {
        userId: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        image: user.image?.image,
        unit: er?.unit || EVENT_RESULT_UNITS.CM,
        calcType: er?.calcType || EVENT_RESULT_CALC_TYPES.AVERAGE,
        skipped: er?.skipped,
        attempts,
      };
    })
  );

  // ── Filtered athletes (search) ────────────────────────────────────────────

  const filteredAthletes = useMemo(() => {
    if (!search) return athletes;
    const s = search.toLowerCase();
    return athletes.filter((a) =>
      `${a.firstName} ${a.lastName}`.toLowerCase().includes(s)
    );
  }, [athletes, search]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAthleteField = (
    userId: string,
    field: keyof Omit<AthleteData, "attempts" | "userId" | "firstName" | "lastName" | "image">,
    value: string | undefined
  ) => {
    setAthletes((prev) =>
      prev.map((a) => (a.userId === userId ? { ...a, [field]: value } : a))
    );
  };

  const handleAttemptField = (
    userId: string,
    attemptIndex: number,
    field: keyof AttemptData,
    value: number | null
  ) => {
    setAthletes((prev) =>
      prev.map((a) => {
        if (a.userId !== userId) return a;
        const newAttempts = a.attempts.map((attempt, i) =>
          i === attemptIndex ? { ...attempt, [field]: value } : attempt
        );
        return { ...a, attempts: newAttempts };
      })
    );
  };

  const selectAllValue = (
    field: "unit" | "calcType",
    value: string
  ) => {
    setAthletes((prev) => prev.map((a) => ({ ...a, [field]: value })));
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const items: any[] = [];

    athletes.forEach((athlete) => {
      if (athlete.skipped) {
        items.push({
          eventId: event._id,
          userId: athlete.userId,
          type: EVENT_RESULT_TYPES.DROP_JUMP,
          skipped: athlete.skipped,
        });
      } else {
        const validAttempts = athlete.attempts.filter(
          (a) => a.boxHeight != null || a.rsi != null
        );
        if (validAttempts.length === 0) return;
        items.push({
          eventId: event._id,
          userId: athlete.userId,
          type: EVENT_RESULT_TYPES.DROP_JUMP,
          dropJumpValue: validAttempts.map((a) => ({
            boxHeight: a.boxHeight ? Number(a.boxHeight) : undefined,
            rsi: a.rsi ? Number(a.rsi) : undefined,
            contactTime: a.contactTime ? Number(a.contactTime) : undefined,
            jumpHeight: a.jumpHeight ? Number(a.jumpHeight) : undefined,
          })),
          unit: athlete.unit,
          calcType: athlete.calcType,
        });
      }
    });

    if (items.length === 0) return;
    setIsLoading(true);
    try {
      const toUpdate: { id: string; body: any; userId: string }[] = [];
      const toCreate: any[] = [];
      const createIndexMap: number[] = [];

      items.forEach((item, i) => {
        const existing = eventResults.find((r) => r.userId === item.userId);
        if (existing) {
          toUpdate.push({ id: existing._id, body: item, userId: item.userId });
        } else {
          createIndexMap.push(i);
          toCreate.push(item);
        }
      });

      const patchResults = await Promise.all(
        toUpdate.map(async ({ id, body, userId }) => {
          try {
            setStatusUser((prev) => ({ ...prev, [userId]: "loading" }));
            await endpoints.patchEventResult(id, body);
            setStatusUser((prev) => ({ ...prev, [userId]: "success" }));
            return true;
          } catch {
            setStatusUser((prev) => ({ ...prev, [userId]: "error" }));
            return false;
          }
        })
      );

      let bulkSuccess = true;
      if (toCreate.length > 0) {
        toCreate.forEach((item) =>
          setStatusUser((prev) => ({ ...prev, [item.userId]: "loading" }))
        );
        const { data: bulkResults } = await endpoints.postEventResultBulk(toCreate);
        bulkResults.forEach((result: any) => {
          const recordIdx = createIndexMap[result.index];
          const userId = items[recordIdx].userId;
          setStatusUser((prev) => ({
            ...prev,
            [userId]: result.success ? "success" : "error",
          }));
          if (!result.success) bulkSuccess = false;
        });
      }

      const hasErrors = patchResults.some((r) => !r) || !bulkSuccess;
      if (hasErrors) {
        toastAlert({ message: "Alcuni risultati non sono stati salvati", type: "error" });
      } else {
        toastAlert({ message: "Compilazione salvata con successo" });
      }
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Toolbar: bulk-set buttons */}
      <div className={style.toolbar}>
        <Typhography size="bold-12" className={style.toolbarLabel}>Unità:</Typhography>
        <CustomButton
          color="outline"
          onClick={() => selectAllValue("unit", EVENT_RESULT_UNITS.CM)}
        >
          Tutti cm
        </CustomButton>
        <CustomButton
          color="outline"
          onClick={() => selectAllValue("unit", EVENT_RESULT_UNITS.IN)}
        >
          Tutti pollici
        </CustomButton>
        <div className={style.toolbarDivider} />
        <Typhography size="bold-12" className={style.toolbarLabel}>Calcolo:</Typhography>
        <CustomButton
          color="outline"
          onClick={() => selectAllValue("calcType", EVENT_RESULT_CALC_TYPES.AVERAGE)}
        >
          Tutti media
        </CustomButton>
        <CustomButton
          color="outline"
          onClick={() => selectAllValue("calcType", EVENT_RESULT_CALC_TYPES.PEAK)}
        >
          Tutti picco
        </CustomButton>
      </div>

      {/* Search */}
      <div className={style.searchBar}>
        <CustomTextInput
          placeholder="Cerca atleta..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Cards */}
      <div className="d-flex flex-column gap-3">
        {filteredAthletes.map((athlete) => {
          const status = statusUser[athlete.userId];
          return (
            <div key={athlete.userId} className={style.card}>
              {/* Card header: avatar + name + status */}
              <div className={style.cardHeader}>
                <div className={style.cardUser}>
                  <CustomAvatar
                    name={`${athlete.firstName[0]}${athlete.lastName[0]}`}
                    src={addBaseUrl(1, athlete.image)}
                  />
                  <Typhography size="bold-14" className={style.userName}>
                    {formatFullName(athlete.firstName, athlete.lastName)}
                  </Typhography>
                </div>
                {status === "success" && (
                  <div className={classNames(style.statusBadge, style.success)}>
                    <CheckCircleIcon size={12} weight="fill" />
                    <Typhography size="medium-12">Salvato</Typhography>
                  </div>
                )}
                {status === "error" && (
                  <div className={classNames(style.statusBadge, style.error)}>
                    <WarningCircleIcon size={12} weight="fill" />
                    <Typhography size="medium-12">Errore</Typhography>
                  </div>
                )}
                {status === "loading" && <Spinner size="xs" />}
              </div>

              {/* Settings: selects */}
              <div className={style.cardSettings}>
                <CustomSelectInput
                  label="Unità"
                  placeholder="Unità"
                  options={EVENT_RESULT_TYPES_OPTIONS}
                  value={athlete.unit}
                  onChange={(v) => handleAthleteField(athlete.userId, "unit", v)}
                />
                <CustomSelectInput
                  label="Calcolo"
                  placeholder="Calcolo"
                  options={EVENT_RESULT_CALC_TYPES_OPTIONS}
                  value={athlete.calcType}
                  onChange={(v) => handleAthleteField(athlete.userId, "calcType", v)}
                />
                <CustomSelectInput
                  label="Assenza"
                  placeholder="Nessuna assenza"
                  options={EVENT_RESULT_SKIP_OPTIONS}
                  value={athlete.skipped}
                  clearOption={{ label: "Nessuna", value: undefined }}
                  onChange={(v) => handleAthleteField(athlete.userId, "skipped", v)}
                />
              </div>

              {/* Card body: 3 attempt rows */}
              <div className={style.nestedGrid}>
                {athlete.attempts.map((attempt, i) => (
                  <div key={i} className={style.nestedRow}>
                    <Typhography size="bold-12" className={style.nestedLabel}>
                      #{i + 1}
                    </Typhography>
                    <div style={{ flex: 1, minWidth: 80 }}>
                      <CustomTextInput
                        label="Alt. Box *"
                        type="number"
                        classNameInput="w-100"
                        disabled={!!athlete.skipped}
                        value={attempt.boxHeight ?? ""}
                        onValueNumberChange={(v) =>
                          handleAttemptField(
                            athlete.userId,
                            i,
                            "boxHeight",
                            isNaN(v.valueAsNumber) ? null : v.valueAsNumber
                          )
                        }
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 80 }}>
                      <CustomTextInput
                        label="RSI *"
                        type="number"
                        classNameInput="w-100"
                        disabled={!!athlete.skipped}
                        value={attempt.rsi ?? ""}
                        onValueNumberChange={(v) =>
                          handleAttemptField(
                            athlete.userId,
                            i,
                            "rsi",
                            isNaN(v.valueAsNumber) ? null : v.valueAsNumber
                          )
                        }
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 80 }}>
                      <CustomTextInput
                        label="T. Contatto"
                        type="number"
                        classNameInput="w-100"
                        disabled={!!athlete.skipped}
                        value={attempt.contactTime ?? ""}
                        onValueNumberChange={(v) =>
                          handleAttemptField(
                            athlete.userId,
                            i,
                            "contactTime",
                            isNaN(v.valueAsNumber) ? null : v.valueAsNumber
                          )
                        }
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 80 }}>
                      <CustomTextInput
                        label="Alt. Salto"
                        type="number"
                        classNameInput="w-100"
                        disabled={!!athlete.skipped}
                        value={attempt.jumpHeight ?? ""}
                        onValueNumberChange={(v) =>
                          handleAttemptField(
                            athlete.userId,
                            i,
                            "jumpHeight",
                            isNaN(v.valueAsNumber) ? null : v.valueAsNumber
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer actions */}
      <div className={style.actionsFooter}>
        <CustomButton color="outline" onClick={onClose} disabled={isLoading}>
          Annulla
        </CustomButton>
        <CustomButton color="secondary" isLoading={isLoading} onClick={handleSave}>
          Conferma
        </CustomButton>
      </div>
    </>
  );
};
